import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Form,
  Input,
  message,
  Spin,
  Result,
  Typography,
  Divider,
  Space,
  Tag,
  Alert
} from 'antd';
import {
  LockOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  VerticalAlignTopOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { addCanvasToPdf } from '../utils/canvasPdf';
import MarkdownContent from '../components/MarkdownContent';
import TravelItinerary from '../components/TravelItinerary';


const { Title, Text } = Typography;

const APP_DOWNLOAD_URL = 'https://wanguo.co.nz/dl';

/**
 * iOS Safari 在输入框聚焦时会放大页面，验证通过切到正文后仍停留在放大状态。
 * 临时锁定 maximum-scale 让 Safari 复位缩放，随后恢复，不影响客人手动双指缩放。
 */
function resetIosZoom() {
  if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const original = meta.getAttribute('content');
  meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
  setTimeout(() => meta.setAttribute('content', original), 400);
}

/**
 * 能否直接把 .pkpass 交给 Apple 钱包：只有 iPhone / iPad 的浏览器可以。
 * App 内 WebView（带 ?v=）与微信内置浏览器点开 .pkpass 没有任何反应，不显示按钮。
 */
function canAddToWallet() {
  const ua = navigator.userAgent || '';
  const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!ios || /MicroMessenger/i.test(ua)) return false;
  return !new URLSearchParams(window.location.search).has('v');
}

/** App 下载引导。App 内 WebView 打开时带 ?v=，此时不显示 */
function AppBanner({ language }) {
  const inApp = new URLSearchParams(window.location.search).has('v');
  // 顶部 110px 用于避开两个 position:fixed 的栏（工具栏 + 分类标签栏），不显示引导时保留占位
  if (inApp) return <div className="no-print" style={{ height: 98 }} />;
  const zh = language === 'zh';
  return (
    <div className="no-print" style={{ maxWidth: '210mm', margin: '110px auto 0', boxSizing: 'border-box' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, background: '#fff',
        padding: '12px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <img src="/app-icon.png" alt="" width={44} height={44} style={{ borderRadius: 10, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e' }}>
            {zh ? '万国旅游 App' : 'Wanguo Travel App'}
          </div>
          <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>
            {zh ? '出团通知书、订单与行程随时查看' : 'Notices, bookings and itineraries in one place'}
          </div>
        </div>
        <Button type="primary" href={APP_DOWNLOAD_URL} style={{ flexShrink: 0 }}>
          {zh ? '下载 App' : 'Get the App'}
        </Button>
      </div>
    </div>
  );
}

// 格式化日期：2026-01-19T00:00:00.000Z -> 2026-01-19
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

// 计算两个日期之间的天数差
const calcNights = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return '-';
  const d1 = new Date(checkIn);
  const d2 = new Date(checkOut);
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : '-';
};

const TouristNotice = () => {
  const { noticeLink } = useParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // 后端对「链接无效」和「通知书尚未创建」返回的是两句不同的话，指向两种不同的处理动作，
  // 前端原来把它们合并显示成「通知书不存在，请检查链接」。这里把后端的原话照实显示。
  const [notFoundMsg, setNotFoundMsg] = useState('');
  // 管理员预览模式：能看见 ≠ 客人能看见，必须在页面上说清楚（见下方提示条）
  const [adminPreview, setAdminPreview] = useState(false);
  const [basicInfo, setBasicInfo] = useState(null);
  const [noticeData, setNoticeData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [language, setLanguage] = useState('zh'); // 'zh' for Chinese, 'en' for English
  const [activeSection, setActiveSection] = useState('');
  const [showBackTop, setShowBackTop] = useState(false);
  const [showItinerary, setShowItinerary] = useState(false);
  const contentRef = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
  // 将相对路径 /api/... 转为绝对路径
  const API_ORIGIN = API_BASE.replace(/\/api$/, '');
  const toAbsUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return `${API_ORIGIN}${url}`;
    return url;
  };

  useEffect(() => {
    fetchBasicInfo();
  }, [noticeLink]);

  const fetchBasicInfo = async () => {
    // ── 管理员预览优先 ───────────────────────────────────────────────
    // 后台「预览」按钮打开的就是这条 URL，和发给客人的链接是同一条，原来一律走 public 接口，
    // 于是【未发布的通知书预览不出来】—— 而预览的全部意义就是发布前先看一眼。
    // admin-preview 已有 requirePermission('notice') 鉴权，且不检查 isPublished、
    // 不要求客人做证件后四位验证，正是预览需要的行为。
    // 客人打开同一条链接时没有登录态，静默回落到下面的公开流程，不泄露任何信息。
    if (AuthService.isAuthenticated()) {
      try {
        const pv = await axios.get(`${API_BASE}/tour-notices/admin-preview/${noticeLink}`,
          { headers: AuthService.getAuthHeaders() });
        if (pv.data?.success) {
          setNoticeData(pv.data.data);
          setVerified(true);
          setAdminPreview(true);
          setLoading(false);
          return;
        }
      } catch (e) {
        // 无 notice 权限 / 登录态过期 → 按客人流程继续，不打断
      }
    }

    try {
      const response = await axios.get(`${API_BASE}/tour-notices/public/${noticeLink}`);
      if (response.data.success) {
        const data = response.data.data;
        // 如果不需要验证，直接设置为已验证并显示通知书
        if (data.requiresVerification === false) {
          setNoticeData(data);
          setVerified(true);
        } else {
          setBasicInfo(data);
          // 免验通道：万国 App 内嵌本页时，客人已在 App 用手机号登录过身份，
          // 由 App 在链接后带 ?v=<证件或手机号后4位> 自动完成验证，避免二次输入。
          // 安全性不降低——等价于客人自己输入那4位，服务端照走 verify 校验与限流。
          const urlV = new URLSearchParams(window.location.search).get('v');
          if (urlV && urlV.length === 4) {
            try {
              if (await doVerify(urlV, { silent: true })) return;
            } catch (e) { /* 落回手工输入 */ }
          }
          // 本次设备此前验证过就直接放行，避免每次打开都要重输
          let saved = null;
          try { saved = localStorage.getItem(`notice_v_${noticeLink}`); } catch (e) { /* 隐私模式 */ }
          if (saved) {
            try {
              const ok = await doVerify(saved, { silent: true });
              // 凭据失效（如护照换新号）就清掉，回落到手工输入
              if (!ok) localStorage.removeItem(`notice_v_${noticeLink}`);
            } catch (e) {
              try { localStorage.removeItem(`notice_v_${noticeLink}`); } catch (_) {}
            }
          }
        }
      }
    } catch (error) {
      setNotFoundMsg(error.response?.data?.message || '');
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // 构建当前可见的分类标签列表
  const buildSections = () => {
    if (!noticeData) return [];
    const { notice, touristHotels, touristAddOns, destination, templates, attachments, touristDocuments, addOnNoticeTemplates: aont } = noticeData;
    const hasHotelPdf = touristDocuments?.some(d => d.documentType === 'hotel');
    const sections = [];

    if (language === 'zh') {
      if (notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0)) {
        sections.push({ id: 'sec-flight', label: '航班信息' });
      }
      if (touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) {
        sections.push({ id: 'sec-hotel', label: '酒店信息' });
      }
      if (touristAddOns?.length > 0) {
        sections.push({ id: 'sec-addons', label: '自费项目' });
      }
      if (aont?.length > 0) {
        sections.push({ id: 'sec-addon-notice', label: '自费详情' });
      }
      if (destination?.gatheringInfo) {
        sections.push({ id: 'sec-gathering', label: '集合与出发' });
      }
      if (destination?.domesticTransportInfo) {
        sections.push({ id: 'sec-domestic-transport', label: '境内交通' });
      }
      if (destination?.entryGuide) {
        sections.push({ id: 'sec-entry', label: '入境指南' });
      }
      if (destination?.customsInfo) {
        sections.push({ id: 'sec-customs', label: '海关申报' });
      }
      if (destination?.vaccineInfo) {
        sections.push({ id: 'sec-vaccine', label: '疫苗说明' });
      }
      if (destination?.localNotices) {
        sections.push({ id: 'sec-local', label: '注意事项' });
      }
      if (destination?.insuranceInfo) {
        sections.push({ id: 'sec-insurance', label: '旅游保险' });
      }
      if (destination?.paymentInfo) {
        sections.push({ id: 'sec-payment', label: '支付信息' });
      }
      if (destination?.weatherInfo) {
        sections.push({ id: 'sec-weather', label: '天气信息' });
      }
      if (templates?.length > 0) {
        templates.forEach((t, i) => {
          sections.push({ id: `sec-tpl-${i}`, label: t.title?.length > 6 ? t.title.slice(0, 6) + '...' : t.title });
        });
      }
      if (notice?.otherNotes) {
        sections.push({ id: 'sec-other', label: '其他注意' });
      }
      if (touristDocuments?.length > 0) {
        sections.push({ id: 'sec-documents', label: '预订文档' });
      }
      if (attachments?.length > 0) {
        sections.push({ id: 'sec-attachments', label: '附件清单' });
      }
    } else {
      if (notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0)) {
        sections.push({ id: 'sec-flight', label: 'Flights' });
      }
      if (touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) {
        sections.push({ id: 'sec-hotel', label: 'Hotels' });
      }
      if (touristAddOns?.length > 0) {
        sections.push({ id: 'sec-addons', label: 'Add-ons' });
      }
      if (aont?.length > 0) {
        sections.push({ id: 'sec-addon-notice', label: 'Add-on Detail' });
      }
      if (destination?.gatheringInfoEn) {
        sections.push({ id: 'sec-gathering', label: 'Gathering' });
      }
      if (destination?.domesticTransportInfoEn) {
        sections.push({ id: 'sec-domestic-transport', label: 'Transport' });
      }
      if (destination?.entryGuideEn) {
        sections.push({ id: 'sec-entry', label: 'Entry Guide' });
      }
      if (destination?.customsInfoEn) {
        sections.push({ id: 'sec-customs', label: 'Customs' });
      }
      if (destination?.vaccineInfoEn) {
        sections.push({ id: 'sec-vaccine', label: 'Vaccine' });
      }
      if (destination?.localNoticesEn) {
        sections.push({ id: 'sec-local', label: 'Local Notes' });
      }
      if (destination?.insuranceInfoEn) {
        sections.push({ id: 'sec-insurance', label: 'Insurance' });
      }
      if (destination?.paymentInfoEn) {
        sections.push({ id: 'sec-payment', label: 'Payment' });
      }
      if (destination?.weatherInfoEn) {
        sections.push({ id: 'sec-weather', label: 'Weather' });
      }
      if (templates?.length > 0) {
        templates.forEach((t, i) => {
          const label = t.titleEn || t.title;
          sections.push({ id: `sec-tpl-${i}`, label: label?.length > 10 ? label.slice(0, 10) + '...' : label });
        });
      }
      if (notice?.otherNotesEn || notice?.otherNotes) {
        sections.push({ id: 'sec-other', label: 'Other Notes' });
      }
      if (touristDocuments?.length > 0) {
        sections.push({ id: 'sec-documents', label: 'Documents' });
      }
      if (attachments?.length > 0) {
        sections.push({ id: 'sec-attachments', label: 'Attachments' });
      }
    }
    return sections;
  };

  const navSections = buildSections();

  // 滚动监听：高亮当前区块 + 显示回到顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowBackTop(window.scrollY > 400);
      if (!noticeData || navSections.length === 0) return;

      const ids = navSections.map(s => s.id);
      let current = '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120) {
            current = id;
          }
        }
      }

      // 页面滚动到底部时，高亮最后一个区块
      const atBottom = (window.innerHeight + window.scrollY) >= (document.body.scrollHeight - 50);
      if (atBottom && ids.length > 0) {
        current = ids[ids.length - 1];
      }

      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navSections.length, language, noticeData]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
      // 立即设置高亮，避免滚动动画期间标签不对
      setActiveSection(id);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 验证凭据按链接隔离存放，避免同一台设备打开不同客人的通知书时串号。
  // 只存后4位（客人自己刚输入的内容），不存任何服务端下发的证件信息。
  const credKey = `notice_v_${noticeLink}`;

  const doVerify = async (last4, { silent = false } = {}) => {
    const response = await axios.post(
      `${API_BASE}/tour-notices/public/${noticeLink}/verify`,
      { passportLast4: last4 }
    );
    if (response.data.success) {
      setNoticeData(response.data.data);
      setVerified(true);
      if (!silent) resetIosZoom();
      try { localStorage.setItem(credKey, last4); } catch (e) { /* 隐私模式下忽略 */ }
      return true;
    }
    if (!silent) message.error('验证失败');
    return false;
  };

  const handleVerify = async (values) => {
    setVerifying(true);
    try {
      await doVerify(values.passportLast4);
    } catch (error) {
      message.error(error.response?.data?.message || '验证失败');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;

    setDownloading(true);
    try {
      const content = contentRef.current;

      // 临时保存当前样式，并应用A4打印样式
      const pages = content.querySelectorAll('[data-pdf-page]');
      const originalStyles = [];

      // 保存原始样式并应用A4固定样式
      pages.forEach((page, index) => {
        originalStyles[index] = page.getAttribute('style');
        page.style.width = '210mm';
        page.style.maxWidth = '210mm';
        page.style.minHeight = 'auto';
        page.style.margin = '0';
        page.style.padding = '15mm 20mm';
        page.style.fontSize = '11pt';
        page.style.boxSizing = 'border-box';
        page.style.boxShadow = 'none';
      });

      // 隐藏表格的滚动容器
      const tableWrappers = content.querySelectorAll('[data-table-wrapper]');
      tableWrappers.forEach((wrapper) => {
        wrapper.style.overflow = 'visible';
      });

      // 修复表格样式
      const tables = content.querySelectorAll('table');
      const originalTableStyles = [];
      tables.forEach((table, index) => {
        originalTableStyles[index] = table.getAttribute('style');
        table.style.minWidth = 'auto';
        table.style.fontSize = '10pt';
      });

      // 保存原始content样式
      const originalContentStyle = content.getAttribute('style');
      content.style.background = '#ffffff';

      // 等待样式应用
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(content, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 794, // A4 宽度 210mm ≈ 794px at 96dpi
        windowWidth: 794
      });

      // 恢复原始样式
      pages.forEach((page, index) => {
        if (originalStyles[index]) {
          page.setAttribute('style', originalStyles[index]);
        }
      });

      tableWrappers.forEach((wrapper) => {
        wrapper.style.overflow = '';
      });

      tables.forEach((table, index) => {
        if (originalTableStyles[index]) {
          table.setAttribute('style', originalTableStyles[index]);
        }
      });

      if (originalContentStyle) {
        content.setAttribute('style', originalContentStyle);
      } else {
        content.removeAttribute('style');
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      addCanvasToPdf(pdf, canvas, { margin: 5 });

      const fileName = `出团通知书_${noticeData?.tourist?.name || 'tourist'}.pdf`;
      pdf.save(fileName);
      message.success(`已开始下载：${fileName}`);
    } catch (error) {
      message.error('PDF生成失败');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notFound) {
    // 「尚未发布 / 尚未创建」是内容还没准备好，不是链接错了。
    // 两者指向的处理动作完全不同：前者去后台点发布，后者去核对链接。
    const notPublished = /未发布|未创建/.test(notFoundMsg);
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Result
          status="404"
          title={notPublished ? '通知书尚未发布' : '通知书不存在'}
          subTitle={notPublished
            ? '该团的出团通知书还未发布，请联系您的旅行社'
            : '请检查链接是否正确，或联系您的旅行社'}
        />
      </div>
    );
  }

  if (!verified) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '16px'
        }}
      >
        <Card style={{ width: '100%', maxWidth: 400, textAlign: 'center' }}>
          <div style={{ marginBottom: 24 }}>
            <LockOutlined style={{ fontSize: 48, color: '#1677ff' }} />
          </div>
          <Title level={4}>出团通知书验证</Title>
          <Text type="secondary">
            您好，{basicInfo?.touristName}！
            <br />
            请输入证件号或手机号后4位以查看您的出团通知书
          </Text>
          <Divider />
          <Form onFinish={handleVerify} layout="vertical">
            <Form.Item
              name="passportLast4"
              rules={[
                { required: true, message: '请输入证件号或手机号后4位' },
                { len: 4, message: '请输入4位字符' }
              ]}
            >
              <Input
                size="large"
                placeholder="后4位"
                maxLength={4}
                inputMode="text"
                autoComplete="off"
                // 字号不得小于 16px，否则 iOS 聚焦时自动放大页面
                style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" size="large" block loading={verifying}>
                验证并查看
              </Button>
            </Form.Item>
          </Form>
          <Text type="secondary" style={{ fontSize: 12 }}>
            产品：{basicInfo?.productName}
          </Text>
          <Divider style={{ margin: '16px 0' }} />
          <Space size={12} style={{ width: '100%', justifyContent: 'center' }}>
            <img src="/app-icon.png" alt="" width={32} height={32} style={{ borderRadius: 8 }} />
            <a href={APP_DOWNLOAD_URL}>下载万国旅游 App</a>
          </Space>
        </Card>
      </div>
    );
  }

  // 已验证，显示完整通知书
  const { tourist, tour, notice, templates, attachments, touristHotels, touristAddOns, addOnNoticeTemplates, touristDocuments, destination, guide, localGuides, tourType, boarding } = noticeData;
  // 只上传了酒店 PDF、未分配结构化酒店时，酒店信息栏显示"详情见PDF附件"
  const hasHotelPdf = touristDocuments?.some(d => d.documentType === 'hotel');

  // 中文数字序号
  const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

  // 检测是否为移动设备
  const isMobile = window.innerWidth <= 768;

  // 一日游通知书
  const isDaytourNotice = tourType === 'daytour';

  // 通用样式 - 自适应布局
  const styles = {
    page: {
      width: '100%',
      maxWidth: '210mm',
      minHeight: isMobile ? 'auto' : '297mm',
      margin: isMobile ? '12px auto 16px' : '16px auto 24px',
      background: '#fff',
      padding: isMobile ? '12px 16px' : '15mm 20mm',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      fontSize: isMobile ? '10pt' : '11pt',
      lineHeight: 1.8,
      color: '#333',
      boxSizing: 'border-box'
    },
    chineseFont: {
      fontFamily: '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Helvetica Neue", sans-serif'
    },
    englishFont: {
      fontFamily: '"Times New Roman", "Georgia", serif'
    },
    sectionTitle: {
      fontSize: isMobile ? '12pt' : '13pt',
      borderBottom: '2px solid #1a5490',
      paddingBottom: '5px',
      marginBottom: '15px',
      color: '#1a5490',
      fontWeight: 'bold'
    },
    tableWrapper: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      marginBottom: '10px'
    },
    tableStyle: {
      width: '100%',
      minWidth: isMobile ? '500px' : 'auto',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '9pt' : '10pt'
    },
    thStyle: {
      border: '1px solid #ddd',
      padding: isMobile ? '6px' : '8px',
      textAlign: 'left',
      background: '#f0f5fa',
      whiteSpace: 'nowrap'
    },
    tdStyle: {
      border: '1px solid #ddd',
      padding: isMobile ? '6px' : '8px'
    }
  };

  // ==================== 一日游通知书渲染 ====================
  if (isDaytourNotice) {
    // 匹配游客上车点的详细信息
    const matchedPickup = (() => {
      if (!tourist?.pickupPoint) return null;
      const points = tour?.pickupPoints || destination?.pickupPoints || [];
      return points.find(p => p.name === tourist.pickupPoint || p.location === tourist.pickupPoint);
    })();

    // 一日游内容板块（按序号排列）
    const daytourSections = [];
    let sectionIdx = 0;
    if (destination?.scheduleRef || destination?.scheduleRefEn) daytourSections.push({ id: 'dt-schedule', zhLabel: '行程时间参考', enLabel: 'Schedule', zh: destination.scheduleRef, en: destination.scheduleRefEn });
    if (destination?.mealArrangement || destination?.mealArrangementEn) daytourSections.push({ id: 'dt-meal', zhLabel: '餐食安排', enLabel: 'Meal Arrangement', zh: destination.mealArrangement, en: destination.mealArrangementEn });
    if (destination?.consumptionInfo || destination?.consumptionInfoEn) daytourSections.push({ id: 'dt-tips', zhLabel: '小费说明', enLabel: 'Tipping Info', zh: destination.consumptionInfo, en: destination.consumptionInfoEn });
    if (destination?.costInclusion || destination?.costInclusionEn) daytourSections.push({ id: 'dt-cost', zhLabel: '费用包含与不包含', enLabel: 'Cost Inclusion', zh: destination.costInclusion, en: destination.costInclusionEn });
    if (destination?.playNotices || destination?.playNoticesEn) daytourSections.push({ id: 'dt-play', zhLabel: '游玩注意事项', enLabel: 'Play Notices', zh: destination.playNotices, en: destination.playNoticesEn });
    if (destination?.importantTips || destination?.importantTipsEn) daytourSections.push({ id: 'dt-important', zhLabel: '重要提示', enLabel: 'Important Tips', zh: destination.importantTips, en: destination.importantTipsEn });

    const dtNavSections = [];
    if (boarding) dtNavSections.push({ id: 'dt-boarding', label: language === 'zh' ? '上车码' : 'Boarding Code' });
    dtNavSections.push({ id: 'dt-guide', label: language === 'zh' ? '领队与导游' : 'Leader & Guide' });
    if (matchedPickup || tourist?.pickupPoint) dtNavSections.push({ id: 'dt-pickup', label: language === 'zh' ? '上车信息' : 'Pickup' });
    daytourSections.forEach(s => dtNavSections.push({ id: s.id, label: language === 'zh' ? s.zhLabel : s.enLabel }));

    const dtStyles = {
      page: {
        width: '100%',
        maxWidth: '210mm',
        minHeight: isMobile ? 'auto' : '297mm',
        margin: isMobile ? '12px auto 16px' : '16px auto 24px',
        background: '#fff',
        padding: isMobile ? '12px 16px' : '15mm 20mm',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        fontSize: isMobile ? '10pt' : '11pt',
        lineHeight: 1.8,
        color: '#333',
        boxSizing: 'border-box',
        fontFamily: language === 'zh'
          ? '"Microsoft YaHei", "微软雅黑", "PingFang SC", "Helvetica Neue", sans-serif'
          : '"Times New Roman", "Georgia", serif'
      },
      sectionTitle: {
        fontSize: isMobile ? '12pt' : '13pt',
        borderBottom: '2px solid #1a5490',
        paddingBottom: '5px',
        marginBottom: '15px',
        color: '#1a5490',
        fontWeight: 'bold'
      }
    };

    return (
      <div style={{ background: '#e8e8e8', minHeight: '100vh' }}>
        {/* 工具栏 */}
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0,
            background: '#1a1a2e',
            padding: isMobile ? '8px 12px' : '12px 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '8px' : '0'
          }}
          className="no-print"
        >
          <Space style={{ justifyContent: isMobile ? 'center' : 'flex-start' }}>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <span style={{ color: '#fff', fontSize: isMobile ? '12px' : '14px' }}>
              {language === 'zh'
                ? `${tourist?.name || '游客'}的一日游通知书`
                : `Day Tour Notice for ${tourist?.name || 'Guest'}`
              }
            </span>
          </Space>
          <Space style={{ justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <Button.Group>
              <Button type={language === 'zh' ? 'primary' : 'default'} onClick={() => setLanguage('zh')} size={isMobile ? 'small' : 'middle'} icon={<GlobalOutlined />}>中文</Button>
              <Button type={language === 'en' ? 'primary' : 'default'} onClick={() => setLanguage('en')} size={isMobile ? 'small' : 'middle'}>English</Button>
            </Button.Group>
            <Button icon={<PrinterOutlined />} onClick={handlePrint} size={isMobile ? 'small' : 'middle'}>
              {language === 'zh' ? '打印' : 'Print'}
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownloadPDF} loading={downloading} size={isMobile ? 'small' : 'middle'}>
              {language === 'zh' ? '下载PDF' : 'Download PDF'}
            </Button>
          </Space>
        </div>

        {/* 导航标签 */}
        {dtNavSections.length > 0 && (
          <div
            style={{
              position: 'fixed',
              top: isMobile ? '72px' : '52px',
              left: 0, right: 0,
              background: '#fff',
              padding: isMobile ? '6px 12px' : '8px 24px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              zIndex: 999,
              display: 'flex', gap: '6px',
              overflowX: 'auto', flexWrap: 'nowrap',
              WebkitOverflowScrolling: 'touch',
            }}
            className="no-print"
          >
            {dtNavSections.map((sec) => (
              <Tag
                key={sec.id}
                onClick={() => {
                  const el = document.getElementById(sec.id);
                  if (el) {
                    const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                color="default"
                style={{ cursor: 'pointer', fontSize: isMobile ? '11px' : '12px', padding: '4px 10px', margin: 0, flexShrink: 0, borderRadius: '16px' }}
              >
                {sec.label}
              </Tag>
            ))}
          </div>
        )}

        {/* 回到顶部 */}
        {showBackTop && (
          <div onClick={scrollToTop} className="no-print" style={{ position: 'fixed', bottom: isMobile ? '24px' : '40px', right: isMobile ? '20px' : '40px', width: '44px', height: '44px', borderRadius: '50%', background: '#1a5490', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 1001, fontSize: '20px' }}>
            <VerticalAlignTopOutlined />
          </div>
        )}

        <AppBanner language={language} />

        {/* 通知书内容 */}
        <div ref={contentRef}>
          <div data-pdf-page="daytour" style={dtStyles.page}>
            {/* 页眉 */}
            <div style={{ borderBottom: '3px double #1a5490', paddingBottom: '10px', marginBottom: isMobile ? '15px' : '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <h1 style={{ fontSize: isMobile ? '20pt' : '26pt', fontWeight: 'bold', color: '#1a5490', margin: '0 0 5px 0', letterSpacing: isMobile ? '5px' : '10px' }}>
                  {language === 'zh' ? '一日游通知书' : 'DAY TOUR NOTICE'}
                </h1>
                <p style={{ fontSize: isMobile ? '10pt' : '11pt', color: '#666', margin: 0 }}>
                  {language === 'zh' ? '万国旅行社 Worldway Travel' : 'Worldway Travel'}
                </p>
              </div>
            </div>

            {/* 1. 头部：产品名称、出团日期 */}
            <div style={{ border: '1px solid #1a5490', padding: isMobile ? '10px 12px' : '15px 20px', marginBottom: isMobile ? '15px' : '25px', background: '#f8fafc' }}>
              {isMobile ? (
                <div style={{ fontSize: '10pt' }}>
                  <div style={{ marginBottom: '8px' }}><strong>{language === 'zh' ? '产品名称：' : 'Product: '}</strong>{tour?.productName}</div>
                  <div><strong>{language === 'zh' ? '出发日期：' : 'Departure Date: '}</strong>{formatDate(tour?.departureDate)}</div>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '5px 0', width: '50%' }}><strong>{language === 'zh' ? '产品名称：' : 'Product: '}</strong>{tour?.productName}</td>
                      <td style={{ padding: '5px 0', width: '50%' }}><strong>{language === 'zh' ? '出发日期：' : 'Departure Date: '}</strong>{formatDate(tour?.departureDate)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* 上车码：会员系统下的一日游订单才有（数据由会员后端提供，见 memberNotify.fetchBoarding） */}
            {boarding && (
              <div id="dt-boarding" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <h2 style={dtStyles.sectionTitle}>{language === 'zh' ? '上车码' : 'Boarding Code'}</h2>
                <div style={{ border: '1px solid #e8e8e8', background: '#fafafa', padding: isMobile ? '14px 12px' : '18px 20px', textAlign: 'center' }}>
                  {boarding.voidReason ? (
                    <div style={{ color: '#cf1322', padding: '12px 0' }}>
                      {language === 'zh' ? `该上车码已停用：${boarding.voidReason}` : `This boarding code is no longer valid: ${boarding.voidReason}`}
                    </div>
                  ) : boarding.boardingCode ? (
                    <>
                      {boarding.qrImage && (
                        <img src={boarding.qrImage} alt={boarding.boardingCode}
                          style={{ width: isMobile ? 168 : 180, height: isMobile ? 168 : 180, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 6, boxSizing: 'border-box', opacity: boarding.boardedAt ? 0.45 : 1 }} />
                      )}
                      <div style={{ marginTop: 8, fontSize: '18pt', fontWeight: 700, letterSpacing: 4, fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}>
                        {boarding.boardingCode}
                      </div>
                      <div style={{ marginTop: 4, color: '#666', fontSize: '10pt' }}>
                        {boarding.boardedAt
                          ? (language === 'zh' ? '已上车' : 'Boarded')
                          : (language === 'zh' ? '上车时向司机出示此二维码' : 'Show this QR code to the driver when boarding')}
                      </div>
                      {canAddToWallet() && boarding.walletUrl && (
                        <a href={boarding.walletUrl} className="no-print" data-html2canvas-ignore="true"
                          style={{ display: 'inline-block', marginTop: 12, lineHeight: 0 }}>
                          <img src="/add-to-apple-wallet.svg" alt={language === 'zh' ? '添加到 Apple 钱包' : 'Add to Apple Wallet'} style={{ height: 44 }} />
                        </a>
                      )}
                    </>
                  ) : (
                    <div style={{ color: '#999', padding: '12px 0' }}>
                      {language === 'zh' ? '上车码生成中' : 'Boarding code is being generated'}
                    </div>
                  )}
                  {boarding.boardingUrl && (
                    <div className="no-print" data-html2canvas-ignore="true" style={{ marginTop: 12, fontSize: '10pt' }}>
                      <a href={boarding.boardingUrl} target="_blank" rel="noopener noreferrer">
                        {language === 'zh' ? '查看上车码页（司机与车辆信息）' : 'Open boarding page (driver and coach details)'}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. 游客信息 */}
            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={dtStyles.sectionTitle}>{language === 'zh' ? '游客信息' : 'Guest Information'}</h2>
              <div style={{ background: '#fafafa', padding: '12px', border: '1px solid #e8e8e8' }}>
                {isMobile ? (
                  <div style={{ fontSize: '10pt' }}>
                    <div style={{ marginBottom: '6px' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{tourist?.name || '-'}</div>
                    {tourist?.orderNumber && <div style={{ marginBottom: '6px' }}><strong>{language === 'zh' ? '订单号：' : 'Order No.: '}</strong>{tourist.orderNumber}</div>}
                    {tourist?.phone && <div><strong>{language === 'zh' ? '联系电话：' : 'Phone: '}</strong>{tourist.phone}</div>}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '5px 0', width: '33%' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{tourist?.name || '-'}</td>
                        <td style={{ padding: '5px 0', width: '33%' }}><strong>{language === 'zh' ? '订单号：' : 'Order No.: '}</strong>{tourist?.orderNumber || '-'}</td>
                        <td style={{ padding: '5px 0', width: '33%' }}><strong>{language === 'zh' ? '联系电话：' : 'Phone: '}</strong>{tourist?.phone || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* 3. 领队与导游信息 */}
              <div id="dt-guide" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <h2 style={dtStyles.sectionTitle}>{language === 'zh' ? '领队与导游信息' : 'Leader & Guide Information'}</h2>
                {!guide && (
                  <div style={{ background: '#fff7e6', padding: '12px 16px', border: '1px solid #ffd591', borderRadius: '4px', color: '#d46b08' }}>
                    {language === 'zh' ? '本产品无领队，请自行值机' : 'No tour leader for this product, please check in by yourself'}
                  </div>
                )}
                {guide && (
                  <div style={{ marginBottom: (localGuides && localGuides.length > 0) ? '10px' : 0 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '10pt' }}>{language === 'zh' ? '领队' : 'Leader'}</div>
                    <div style={{ background: '#e6f7ff', padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                      {isMobile ? (
                        <div style={{ fontSize: '10pt' }}>
                          <div style={{ marginBottom: '4px' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{guide.name}</div>
                          {guide.phone && <div style={{ marginBottom: '4px' }}><strong>{language === 'zh' ? '电话：' : 'Phone: '}</strong>{guide.phone}</div>}
                          {guide.wechat && <div><strong>{language === 'zh' ? '微信：' : 'WeChat: '}</strong>{guide.wechat}</div>}
                        </div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{guide.name}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '电话：' : 'Phone: '}</strong>{guide.phone || '-'}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '微信：' : 'WeChat: '}</strong>{guide.wechat || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
                {localGuides && localGuides.length > 0 && (
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '10pt' }}>{language === 'zh' ? '导游' : 'Guide'}</div>
                    {localGuides.map((lg, idx) => (
                      <div key={idx} style={{ background: '#f6ffed', padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: idx < localGuides.length - 1 ? '8px' : 0 }}>
                        {isMobile ? (
                          <div style={{ fontSize: '10pt' }}>
                            <div style={{ marginBottom: '4px' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{lg.name}</div>
                            {lg.phone && <div style={{ marginBottom: '4px' }}><strong>{language === 'zh' ? '电话：' : 'Phone: '}</strong>{lg.phone}</div>}
                            {lg.note && <div><strong>{language === 'zh' ? '备注：' : 'Note: '}</strong>{lg.note}</div>}
                          </div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              <tr>
                                <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '姓名：' : 'Name: '}</strong>{lg.name}</td>
                                <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '电话：' : 'Phone: '}</strong>{lg.phone || '-'}</td>
                                <td style={{ padding: '3px 0', width: '33%' }}><strong>{language === 'zh' ? '备注：' : 'Note: '}</strong>{lg.note || '-'}</td>
                              </tr>
                            </tbody>
                          </table>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            {/* 4. 上车信息 */}
            {(matchedPickup || tourist?.pickupPoint) && (
              <div id="dt-pickup" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <h2 style={dtStyles.sectionTitle}>{language === 'zh' ? '上车信息' : 'Pickup Information'}</h2>
                <div style={{ background: '#fff8e6', padding: '12px', border: '1px solid #ffd666', borderRadius: '4px' }}>
                  <div style={{ fontSize: isMobile ? '10pt' : '11pt' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>{language === 'zh' ? '上车点：' : 'Pickup Point: '}</strong>
                      <span style={{ color: '#1a5490', fontWeight: 'bold' }}>{tourist?.pickupPoint}</span>
                    </div>
                    {matchedPickup?.time && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>{language === 'zh' ? '上车时间：' : 'Pickup Time: '}</strong>
                        <span style={{ color: '#d4380d', fontWeight: 'bold', fontSize: '12pt' }}>{matchedPickup.time}</span>
                      </div>
                    )}
                    {matchedPickup?.address && (
                      <div>
                        <strong>{language === 'zh' ? '详细地址：' : 'Address: '}</strong>{matchedPickup.address}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 5-10. 各内容板块 */}
            {daytourSections.map((section, idx) => {
              let baseNum = 1;
              if (guide) baseNum++;
              if (matchedPickup || tourist?.pickupPoint) baseNum++;
              baseNum++; // 游客信息
              const secNum = baseNum + idx;
              const content = language === 'zh' ? section.zh : (section.en || section.zh);
              if (!content) return null;
              return (
                <div key={section.id} id={section.id} style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                  <h2 style={dtStyles.sectionTitle}>
                    {language === 'zh' ? section.zhLabel : section.enLabel}
                  </h2>
                  <MarkdownContent text={content} />
                </div>
              );
            })}

            {/* 页脚 */}
            <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '3px double #1a5490', textAlign: 'center' }}>
              <p style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a5490', marginBottom: '15px' }}>
                {language === 'zh' ? '祝您旅途愉快！' : 'Have a Pleasant Journey!'}
              </p>
              <div style={{ fontSize: '10pt', color: '#666', lineHeight: 1.6 }}>
                <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#1a5490' }}>
                  {language === 'zh' ? '万国旅行社 Worldway Travel' : 'Worldway Travel'}
                </p>
                <p style={{ margin: '5px 0' }}>
                  {language === 'zh' ? '地址：' : 'Address: '}14C/155 Queen Street Auckland Central, Auckland, New Zealand
                </p>
                <p style={{ margin: '5px 0' }}>
                  {language === 'zh' ? '电话：' : 'Tel: '}0800 116 688 | {language === 'zh' ? '邮箱：' : 'Email: '}confirmation@worldway.co.nz
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 打印样式 */}
        <style>{`
          @media print {
            .no-print { display: none !important; }
            body { background: white !important; margin: 0 !important; padding: 0 !important; }
            @page { size: A4; margin: 10mm; }
          }
        `}</style>
      </div>
    );
  }

  // 出境游英文行程单（过海关用）
  const itinerary = noticeData.itinerary;
  if (showItinerary && itinerary?.complete) {
    return (
      <TravelItinerary
        data={noticeData}
        isMobile={isMobile}
        onBack={() => { setShowItinerary(false); window.scrollTo(0, 0); }}
      />
    );
  }

  return (
    <div style={{ background: '#e8e8e8', minHeight: '100vh' }}>
      {/* 工具栏 - 自适应 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: '#1a1a2e',
          padding: isMobile ? '8px 12px' : '12px 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '8px' : '0'
        }}
        className="no-print"
      >
        <Space style={{ justifyContent: isMobile ? 'center' : 'flex-start' }}>
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
          <span style={{ color: '#fff', fontSize: isMobile ? '12px' : '14px' }}>
            {language === 'zh'
              ? (isMobile ? `${tourist.passportName || tourist.name}的出团通知书` : `您好，${tourist.passportName || tourist.name}！这是您的出团通知书`)
              : (isMobile ? `Notice for ${tourist.passportName || tourist.name}` : `Hello, ${tourist.passportName || tourist.name}! Here is your departure notice`)
            }
          </span>
        </Space>
        <Space style={{ justifyContent: isMobile ? 'center' : 'flex-end' }}>
          {/* 语言切换按钮 */}
          <Button.Group>
            <Button
              type={language === 'zh' ? 'primary' : 'default'}
              onClick={() => setLanguage('zh')}
              size={isMobile ? 'small' : 'middle'}
              icon={<GlobalOutlined />}
            >
              中文
            </Button>
            <Button
              type={language === 'en' ? 'primary' : 'default'}
              onClick={() => setLanguage('en')}
              size={isMobile ? 'small' : 'middle'}
            >
              English
            </Button>
          </Button.Group>
          {itinerary?.complete && (
            <Button
              icon={<FileTextOutlined />}
              onClick={() => { setShowItinerary(true); window.scrollTo(0, 0); }}
              size={isMobile ? 'small' : 'middle'}
            >
              {language === 'zh' ? '英文行程单' : 'Travel Itinerary'}
            </Button>
          )}
          {!(isMobile && itinerary?.complete) && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint} size={isMobile ? 'small' : 'middle'}>
              {language === 'zh' ? '打印' : 'Print'}
            </Button>
          )}
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
            size={isMobile ? 'small' : 'middle'}
          >
            {language === 'zh' ? '下载PDF' : 'Download PDF'}
          </Button>
        </Space>
      </div>

      {/* 分类标签导航栏 */}
      {navSections.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '72px' : '52px',
            left: 0,
            right: 0,
            background: '#fff',
            padding: isMobile ? '6px 12px' : '8px 24px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            zIndex: 999,
            display: 'flex',
            gap: '6px',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            WebkitOverflowScrolling: 'touch',
          }}
          className="no-print"
        >
          {navSections.map((sec) => (
            <Tag
              key={sec.id}
              onClick={() => scrollToSection(sec.id)}
              color={activeSection === sec.id ? 'blue' : 'default'}
              style={{
                cursor: 'pointer',
                fontSize: isMobile ? '11px' : '12px',
                padding: '4px 10px',
                margin: 0,
                flexShrink: 0,
                borderRadius: '16px',
                fontWeight: activeSection === sec.id ? 'bold' : 'normal',
                transition: 'all 0.3s',
              }}
            >
              {sec.label}
            </Tag>
          ))}
        </div>
      )}

      {/* 一键回到顶部按钮 */}
      {showBackTop && (
        <div
          onClick={scrollToTop}
          className="no-print"
          style={{
            position: 'fixed',
            bottom: isMobile ? '24px' : '40px',
            right: isMobile ? '20px' : '40px',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: '#1a5490',
            color: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 1001,
            fontSize: '20px',
          }}
          title={language === 'zh' ? '回到顶部' : 'Back to Top'}
        >
          <VerticalAlignTopOutlined />
        </div>
      )}

      <AppBanner language={language} />

      {/* 管理员预览提示条。走正常文档流不做浮层（避免遮挡正文），
          打印/导出 PDF 时由 no-print 隐藏。 */}

      {adminPreview && (
        // 固定栏的避让由上方 AppBanner 负责
        <div className="no-print" style={{ maxWidth: '210mm', margin: '12px auto 0', padding: '0 16px' }}>
          <Alert
            type="info"
            showIcon
            message="管理员预览"
            description="当前为管理员预览视图，客人打开同一链接需先通过证件验证。"
          />
        </div>
      )}

      {/* 通知书内容 */}
      <div ref={contentRef}>

        {/* ==================== 中文版本 ==================== */}
        {language === 'zh' && (
        <div data-pdf-page="cn" style={{ ...styles.page, ...styles.chineseFont, ...(adminPreview ? { marginTop: '16px' } : {}) }}>
          {/* 中文页眉 */}
          <div style={{
            borderBottom: '3px double #1a5490',
            paddingBottom: '10px',
            marginBottom: isMobile ? '15px' : '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: isMobile ? '20pt' : '26pt',
                fontWeight: 'bold',
                color: '#1a5490',
                margin: '0 0 5px 0',
                letterSpacing: isMobile ? '5px' : '10px'
              }}>
                出团通知书
              </h1>
              <p style={{
                fontSize: isMobile ? '10pt' : '11pt',
                color: '#666',
                margin: 0
              }}>
                万国旅行社 Worldway Travel
              </p>
            </div>
          </div>

          {/* 中文基本信息 */}
          <div style={{
            border: '1px solid #1a5490',
            padding: isMobile ? '10px 12px' : '15px 20px',
            marginBottom: isMobile ? '15px' : '25px',
            background: '#f8fafc'
          }}>
            {isMobile ? (
              // 移动端：垂直布局
              <div style={{ fontSize: '10pt' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>游客姓名：</strong>
                  <span style={{ wordBreak: 'break-word' }}>{tourist?.passportName || tourist?.name || '待定'}</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>护照号码：</strong>{tourist?.passportNumber || '-'}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>产品名称：</strong>{tour?.productName}
                </div>
                <div>
                  <strong>出发日期：</strong>{formatDate(tour?.departureDate)}
                </div>
              </div>
            ) : (
              // 桌面端：水平表格布局
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 0', width: '30%', verticalAlign: 'top' }}>
                      <strong>游客姓名：</strong><br/>
                      <span style={{ wordBreak: 'break-word' }}>{tourist?.passportName || tourist?.name || '待定'}</span>
                    </td>
                    <td style={{ padding: '5px 0', width: '20%', verticalAlign: 'top' }}>
                      <strong>护照号码：</strong><br/>
                      {tourist?.passportNumber || '-'}
                    </td>
                    <td style={{ padding: '5px 0', width: '28%', verticalAlign: 'top' }}>
                      <strong>产品名称：</strong><br/>
                      {tour?.productName}
                    </td>
                    <td style={{ padding: '5px 0', width: '22%', verticalAlign: 'top' }}>
                      <strong>出发日期：</strong><br/>
                      {formatDate(tour?.departureDate)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* 中文 - 领队与导游信息 */}
            <div id="sec-guide" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>领队与导游信息</h2>
              {!guide && (
                <div style={{ background: '#fff7e6', padding: '12px 16px', border: '1px solid #ffd591', borderRadius: '4px', color: '#d46b08' }}>
                  本产品无领队，请自行值机
                </div>
              )}
              {guide && (
                <div style={{ marginBottom: (localGuides && localGuides.length > 0) ? '12px' : 0 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>领队</div>
                  <div style={{ background: '#e6f7ff', padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                    {isMobile ? (
                      <div style={{ fontSize: '10pt' }}>
                        <div style={{ marginBottom: '4px' }}><strong>姓名：</strong>{guide.name}</div>
                        {guide.phone && <div style={{ marginBottom: '4px' }}><strong>电话：</strong>{guide.phone}</div>}
                        {guide.wechat && <div><strong>微信：</strong>{guide.wechat}</div>}
                      </div>
                    ) : (
                      <table style={{ width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>姓名：</strong>{guide.name}</td>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>电话：</strong>{guide.phone || '-'}</td>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>微信：</strong>{guide.wechat || '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
              {localGuides && localGuides.length > 0 && (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>导游</div>
                  {localGuides.map((lg, idx) => (
                    <div key={idx} style={{ background: '#f6ffed', padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: idx < localGuides.length - 1 ? '8px' : 0 }}>
                      {isMobile ? (
                        <div style={{ fontSize: '10pt' }}>
                          <div style={{ marginBottom: '4px' }}><strong>姓名：</strong>{lg.name}</div>
                          {lg.phone && <div style={{ marginBottom: '4px' }}><strong>电话：</strong>{lg.phone}</div>}
                          {lg.note && <div><strong>备注：</strong>{lg.note}</div>}
                        </div>
                      ) : (
                        <table style={{ width: '100%' }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>姓名：</strong>{lg.name}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>电话：</strong>{lg.phone || '-'}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>备注：</strong>{lg.note || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {notice?.aucklandMeetingInfo && (
                <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  <strong style={{ color: '#d48806' }}>备注：</strong>{notice.aucklandMeetingInfo}
                </div>
              )}
            </div>

          {/* 中文 - 航班信息 */}
          {notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0) && (
            <div id="sec-flight" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>
                机场及航班信息
              </h2>
              <p style={{ marginBottom: '10px', fontSize: isMobile ? '9pt' : '11pt' }}>
                机票详情请查看附件。使用护照原件自行值机。
              </p>

              <div data-table-wrapper style={styles.tableWrapper}>
                <table style={styles.tableStyle}>
                  <thead>
                    <tr>
                      <th style={styles.thStyle}>出发日期</th>
                      <th style={styles.thStyle}>航线</th>
                      <th style={styles.thStyle}>航班号</th>
                      <th style={styles.thStyle}>起飞时间</th>
                      <th style={styles.thStyle}>到达时间</th>
                      <th style={styles.thStyle}>行李额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(notice.flightInfo.outbound || []), ...(notice.flightInfo.inbound || [])].map((flight, index) => {
                      // 判断到达是否跨天：比较出发日期和到达日期
                      let arrivalDisplay = flight.arrivalTime || '';
                      if (flight.arrivalDate && flight.date && flight.arrivalDate !== flight.date) {
                        arrivalDisplay = `${flight.arrivalTime} (+1天)`;
                      }
                      return (
                        <tr key={index}>
                          <td style={styles.tdStyle}>{flight.date}</td>
                          <td style={styles.tdStyle}>{flight.fromCity || flight.from} → {flight.toCity || flight.to}</td>
                          <td style={styles.tdStyle}>{flight.flightNo || flight.flight}</td>
                          <td style={styles.tdStyle}>{flight.time}</td>
                          <td style={styles.tdStyle}>{arrivalDisplay}</td>
                          <td style={styles.tdStyle}>{flight.baggage || '2件'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>


              {notice.flightInfo.baggageNote && (
                <p style={{ background: '#fff8e6', padding: '10px', border: '1px solid #ffd666', fontSize: '10pt' }}>
                  <strong>行李说明：</strong>{notice.flightInfo.baggageNote}
                </p>
              )}
              {notice.flightInfo.additionalNote && (
                <p style={{ background: '#fff1f0', padding: '10px', border: '1px solid #ffa39e', fontSize: '10pt', marginTop: '10px' }}>
                  <strong>特别注意：</strong>{notice.flightInfo.additionalNote}
                </p>
              )}
            </div>
          )}

          {/* 中文 - 酒店信息 */}
          {(touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) && (
            <div id="sec-hotel" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>
                酒店信息
              </h2>

              {touristHotels?.length > 0 ? (
                <>
                  <div data-table-wrapper style={styles.tableWrapper}>
                  <table style={{ ...styles.tableStyle, minWidth: isMobile ? '600px' : 'auto' }}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>入住日期</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>天数</th>
                        <th style={styles.thStyle}>酒店名称</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>星级</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>含早</th>
                        <th style={styles.thStyle}>电话</th>
                        <th style={styles.thStyle}>预订号</th>
                      </tr>
                    </thead>
                    <tbody>
                      {touristHotels.map((th, index) => (
                        <tr key={th.id || index}>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.checkInDate}</td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>{calcNights(th.checkInDate, th.checkOutDate)}</td>
                          <td style={styles.tdStyle}>
                            {th.hotel?.name}
                            {th.hotel?.address && (
                              <div style={{ fontSize: isMobile ? '7pt' : '8pt', color: '#999', marginTop: '2px' }}>{th.hotel.address}</div>
                            )}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center', color: '#faad14' }}>
                            {th.hotel?.starLevel ? '★'.repeat(th.hotel.starLevel) : '-'}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>
                            {th.hotel?.hasBreakfast ? '含早' : '不含'}
                          </td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.hotel?.phone || '-'}</td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            {th.bookingNumber || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* 酒店交通信息（去重） */}
                  {touristHotels.some(th => th.hotel?.transportation) && (() => {
                    const groups = {};
                    touristHotels.filter(th => th.hotel?.transportation).forEach(th => {
                      const info = th.hotel.transportation;
                      if (!groups[info]) groups[info] = [];
                      groups[info].push(th.hotel.name);
                    });
                    return (
                      <div style={{ marginTop: '15px', padding: isMobile ? '8px' : '10px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                        <strong style={{ color: '#1a5490' }}>酒店交通信息（以实际情况为准）：</strong>
                        <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: isMobile ? '9pt' : '10pt' }}>
                          {Object.entries(groups).map(([info, names], index) => (
                            <li key={index} style={{ marginBottom: '5px' }}>
                              <strong>{names.join('、')}：</strong>{info}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                  {/* 酒店注意事项（去重） */}
                  {touristHotels.some(th => th.hotel?.notes) && (() => {
                    const groups = {};
                    touristHotels.filter(th => th.hotel?.notes).forEach(th => {
                      const note = th.hotel.notes;
                      if (!groups[note]) groups[note] = [];
                      groups[note].push(th.hotel.name);
                    });
                    return (
                      <div style={{ marginTop: '15px', padding: isMobile ? '8px' : '10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px' }}>
                        <strong style={{ color: '#d48806' }}>酒店注意事项：</strong>
                        <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: isMobile ? '9pt' : '10pt' }}>
                          {Object.entries(groups).map(([note, names], index) => (
                            <li key={index} style={{ marginBottom: '5px' }}>
                              <strong>{names.join('、')}：</strong>{note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </>
              ) : notice?.hotelInfo?.length > 0 ? (
                <div data-table-wrapper style={styles.tableWrapper}>
                  <table style={styles.tableStyle}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>城市</th>
                        <th style={styles.thStyle}>酒店名称</th>
                        <th style={styles.thStyle}>地址</th>
                        <th style={styles.thStyle}>电话</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notice.hotelInfo.map((hotel, index) => (
                        <tr key={index}>
                          <td style={styles.tdStyle}>{hotel.city}</td>
                          <td style={styles.tdStyle}>{hotel.name}</td>
                          <td style={{ ...styles.tdStyle, fontSize: isMobile ? '8pt' : '9pt' }}>{hotel.address}</td>
                          <td style={styles.tdStyle}>{hotel.phone || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : hasHotelPdf && (
                <div style={{ padding: isMobile ? '10px' : '12px 15px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px', fontSize: isMobile ? '9pt' : '10pt', color: '#389e0d' }}>
                  酒店详情见PDF附件（见下方"您的预订文档"）
                </div>
              )}
            </div>
          )}

          {/* 中文 - 自费项目 */}
          {touristAddOns?.length > 0 && (
            <div id="sec-addons" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                自费项目
              </h2>
              {touristAddOns.map((ta, index) => (
                <div key={ta.id || index} style={{
                  border: '1px solid #e8e8e8',
                  padding: '12px',
                  marginBottom: '10px',
                  background: '#fafafa'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong style={{ color: '#1a5490' }}>
                      第{ta.addOnProduct?.dayNumber}天：{ta.addOnProduct?.name}
                    </strong>
                  </p>
                  {ta.addOnProduct?.meetingTime && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>集合时间：</strong>{ta.addOnProduct.meetingTime}
                      {ta.addOnProduct?.duration && ` | 时长：${ta.addOnProduct.duration}`}
                    </p>
                  )}
                  {ta.addOnProduct?.meetingPlace && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>集合地点：</strong>{ta.addOnProduct.meetingPlace}
                    </p>
                  )}
                  {ta.addOnProduct?.summary && (
                    <p style={{ margin: '5px 0', fontSize: '10pt', color: '#666' }}>
                      {ta.addOnProduct.summary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 中文 - Add-On通知书模板 */}
          {addOnNoticeTemplates?.length > 0 && addOnNoticeTemplates.map((template, index) => (
            <div key={template.id} id={index === 0 ? 'sec-addon-notice' : undefined} style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                自费项目详情：{template.productName}
              </h2>

              {/* 集合信息 */}
              {(template.meetingTime || template.meetingPlace) && (
                <div style={{
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '15px'
                }}>
                  {template.meetingTime && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>集合时间：</strong>{template.meetingTime}
                    </p>
                  )}
                  {template.meetingPlace && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>集合地点：</strong>{template.meetingPlace}
                    </p>
                  )}
                </div>
              )}

              {/* 机票信息 */}
              {template.hasFlightInfo && template.flightDetails && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【机票信息】
                  </p>
                  <div style={{
                    background: '#fff8e6',
                    border: '1px solid #ffd666',
                    padding: '12px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {template.flightDetails}
                  </div>
                </div>
              )}

              {/* 行程安排 */}
              {template.itinerary && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【行程安排】
                  </p>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {template.itinerary}
                  </div>
                </div>
              )}

              {/* 注意事项 */}
              {template.notes && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【注意事项】
                  </p>
                  <div style={{
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '12px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {template.notes}
                  </div>
                </div>
              )}

              {/* 附件下载 */}
              {template.attachments?.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【相关附件】
                  </p>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    {template.attachments.map((att) => (
                      <li key={att.id} style={{ marginBottom: '5px' }}>
                        {att.isDownloadable && att.signedUrl ? (
                          <a
                            href={toAbsUrl(att.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {att.name}
                          </a>
                        ) : (
                          <span>{att.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {/* 中文 - 目的地信息 */}
          {destination && (destination.gatheringInfo || destination.domesticTransportInfo || destination.entryGuide || destination.customsInfo || destination.vaccineInfo || destination.localNotices || destination.insuranceInfo || destination.paymentInfo || destination.weatherInfo) && (
            <>
              {destination.gatheringInfo && (
                <div id="sec-gathering" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>集合与出发说明</h2>
                  <MarkdownContent text={destination.gatheringInfo} />
                </div>
              )}

              {destination.domesticTransportInfo && (
                <div id="sec-domestic-transport" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>境内交通安排</h2>
                  <MarkdownContent text={destination.domesticTransportInfo} />
                </div>
              )}

              {destination.entryGuide && (
                <div id="sec-entry" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    入境指南
                  </h2>
                  <MarkdownContent text={destination.entryGuide} />
                </div>
              )}

              {destination.customsInfo && (
                <div id="sec-customs" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    海关申报说明
                  </h2>
                  <MarkdownContent text={destination.customsInfo} />
                </div>
              )}

              {destination.vaccineInfo && (
                <div id="sec-vaccine" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    疫苗说明
                  </h2>
                  <MarkdownContent text={destination.vaccineInfo} />
                </div>
              )}

              {destination.localNotices && (
                <div id="sec-local" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>
                    注意事项
                  </h2>
                  <MarkdownContent text={destination.localNotices} />
                </div>
              )}

              {destination.insuranceInfo && (
                <div id="sec-insurance" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>旅游保险说明</h2>
                  <MarkdownContent text={destination.insuranceInfo} />
                </div>
              )}

              {destination.paymentInfo && (
                <div id="sec-payment" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>支付信息</h2>
                  <MarkdownContent text={destination.paymentInfo} />
                </div>
              )}

              {destination.weatherInfo && (
                <div id="sec-weather" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>天气信息</h2>
                  <MarkdownContent text={destination.weatherInfo} />
                </div>
              )}
            </>
          )}

          {/* 中文 - 通用模板内容 */}
          {templates?.map((template, index) => {
            return (
              <div key={template.id} id={`sec-tpl-${index}`} style={{ marginBottom: '25px' }}>
                <h2 style={styles.sectionTitle}>
                  {template.title}
                </h2>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {template.content}
                </div>
              </div>
            );
          })}

          {/* 中文 - 其他注意事项 */}
          {notice?.otherNotes && (
            <div id="sec-other" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>其他注意事项</h2>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {notice.otherNotes}
              </div>
            </div>
          )}

          {/* 中文 - 您的预订文档 */}
          {touristDocuments?.length > 0 && (
            <div id="sec-documents" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>您的预订文档</h2>
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '10px'
              }}>
                <p style={{ margin: '0 0 10px 0', color: '#52c41a', fontWeight: 'bold' }}>
                  请点击下载您的预订确认文件：
                </p>
                {/* 酒店 */}
                {touristDocuments.filter(d => d.documentType === 'hotel').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【酒店】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'hotel').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 机票 */}
                {touristDocuments.filter(d => d.documentType === 'flight').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【机票】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'flight').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Add-ons */}
                {touristDocuments.filter(d => d.documentType === 'addon').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【附加项目文档】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'addon').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* 附件 */}
                {touristDocuments.filter(d => d.documentType === 'attachment').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【附件】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'attachment').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 中文 - 附件清单 */}
          {attachments?.length > 0 && (
            <div id="sec-attachments" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>附件清单</h2>
              {attachments.map((att) => (
                <div key={att.id} style={{ marginBottom: '20px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    {att.name}
                    {att.description && <span style={{ fontWeight: 'normal', color: '#666' }}> - {att.description}</span>}
                  </p>
                  {att.fileType === 'image' && (att.signedUrl || att.filePath) && (
                    <img
                      src={toAbsUrl(att.signedUrl || att.filePath)}
                      alt={att.name}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                  {att.fileType === 'pdf' && (att.signedUrl || att.filePath) && (
                    <a
                      href={toAbsUrl(att.signedUrl || att.filePath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1677ff' }}
                    >
                      点击查看PDF文件
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}


          {/* 中文页脚 */}
          <div style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '3px double #1a5490',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a5490', marginBottom: '15px' }}>
              祝您旅途愉快！
            </p>
            <div style={{ fontSize: '10pt', color: '#666', lineHeight: 1.6 }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#1a5490' }}>
                万国旅行社 Worldway Travel
              </p>
              <p style={{ margin: '5px 0' }}>
                地址：14C/155 Queen Street Auckland Central, Auckland, New Zealand
              </p>
              <p style={{ margin: '5px 0' }}>
                电话：0800 116 688 | 邮箱：confirmation@worldway.co.nz
              </p>
            </div>
          </div>
        </div>
        )}

        {/* ==================== 英文版本 ==================== */}
        {language === 'en' && (
        <div data-pdf-page="en" style={{ ...styles.page, ...styles.englishFont, marginTop: isMobile ? '20px' : '30px' }}>
          {/* English Header */}
          <div style={{
            borderBottom: '3px double #1a5490',
            paddingBottom: '10px',
            marginBottom: isMobile ? '15px' : '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: isMobile ? '16pt' : '24pt',
                fontWeight: 'bold',
                color: '#1a5490',
                margin: '0 0 5px 0',
                letterSpacing: isMobile ? '1px' : '3px'
              }}>
                GROUP DEPARTURE NOTICE
              </h1>
              <p style={{
                fontSize: isMobile ? '10pt' : '11pt',
                color: '#666',
                margin: 0
              }}>
                Worldway Travel
              </p>
            </div>
          </div>

          {/* English Basic Info */}
          <div style={{
            border: '1px solid #1a5490',
            padding: isMobile ? '10px 12px' : '15px 20px',
            marginBottom: isMobile ? '15px' : '25px',
            background: '#f8fafc'
          }}>
            {isMobile ? (
              // 移动端：垂直布局
              <div style={{ fontSize: '10pt' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Guest Name:</strong> {tourist?.passportName || tourist?.name || 'TBA'}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Passport No.:</strong> {tourist?.passportNumber || '-'}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Product:</strong> {tour?.productName}
                </div>
                <div>
                  <strong>Departure Date:</strong> {formatDate(tour?.departureDate)}
                </div>
              </div>
            ) : (
              // 桌面端：水平表格布局
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '5px 0', width: '25%' }}>
                      <strong>Guest Name:</strong><br/>
                      {tourist?.passportName || tourist?.name || 'TBA'}
                    </td>
                    <td style={{ padding: '5px 0', width: '25%' }}>
                      <strong>Passport No.:</strong><br/>
                      {tourist?.passportNumber || '-'}
                    </td>
                    <td style={{ padding: '5px 0', width: '25%' }}>
                      <strong>Product:</strong><br/>
                      {tour?.productName}
                    </td>
                    <td style={{ padding: '5px 0', width: '25%' }}>
                      <strong>Departure Date:</strong><br/>
                      {formatDate(tour?.departureDate)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          {/* English - Leader & Guide Information */}
            <div id="sec-guide" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>Leader & Guide Information</h2>
              {!guide && (
                <div style={{ background: '#fff7e6', padding: '12px 16px', border: '1px solid #ffd591', borderRadius: '4px', color: '#d46b08' }}>
                  No tour leader for this product, please check in by yourself
                </div>
              )}
              {guide && (
                <div style={{ marginBottom: (localGuides && localGuides.length > 0) ? '12px' : 0 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Leader</div>
                  <div style={{ background: '#e6f7ff', padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                    {isMobile ? (
                      <div style={{ fontSize: '10pt' }}>
                        <div style={{ marginBottom: '4px' }}><strong>Name: </strong>{guide.name}</div>
                        {guide.phone && <div style={{ marginBottom: '4px' }}><strong>Phone: </strong>{guide.phone}</div>}
                        {guide.wechat && <div><strong>WeChat: </strong>{guide.wechat}</div>}
                      </div>
                    ) : (
                      <table style={{ width: '100%' }}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>Name: </strong>{guide.name}</td>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>Phone: </strong>{guide.phone || '-'}</td>
                            <td style={{ padding: '3px 0', width: '33%' }}><strong>WeChat: </strong>{guide.wechat || '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
              {localGuides && localGuides.length > 0 && (
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>Guide</div>
                  {localGuides.map((lg, idx) => (
                    <div key={idx} style={{ background: '#f6ffed', padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: '4px', marginBottom: idx < localGuides.length - 1 ? '8px' : 0 }}>
                      {isMobile ? (
                        <div style={{ fontSize: '10pt' }}>
                          <div style={{ marginBottom: '4px' }}><strong>Name: </strong>{lg.name}</div>
                          {lg.phone && <div style={{ marginBottom: '4px' }}><strong>Phone: </strong>{lg.phone}</div>}
                          {lg.note && <div><strong>Note: </strong>{lg.note}</div>}
                        </div>
                      ) : (
                        <table style={{ width: '100%' }}>
                          <tbody>
                            <tr>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>Name: </strong>{lg.name}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>Phone: </strong>{lg.phone || '-'}</td>
                              <td style={{ padding: '3px 0', width: '33%' }}><strong>Note: </strong>{lg.note || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(notice?.aucklandMeetingInfoEn || notice?.aucklandMeetingInfo) && (
                <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  <strong style={{ color: '#d48806' }}>Note: </strong>{notice.aucklandMeetingInfoEn || notice.aucklandMeetingInfo}
                </div>
              )}
            </div>

          {/* English - Flight Information */}
          {notice?.flightInfo && (notice.flightInfo.outbound?.length > 0 || notice.flightInfo.inbound?.length > 0) && (
            <div id="sec-flight" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>Airport & Flight Information</h2>
              <p style={{ marginBottom: '10px', fontSize: isMobile ? '9pt' : '11pt' }}>
                Please check the attached ticket details. No tour leader for this product.
                Please arrive at the airport 3 hours early and check in with your passport.
              </p>

              <div data-table-wrapper style={styles.tableWrapper}>
                <table style={styles.tableStyle}>
                  <thead>
                    <tr>
                      <th style={styles.thStyle}>Date</th>
                      <th style={styles.thStyle}>Route</th>
                      <th style={styles.thStyle}>Flight</th>
                      <th style={styles.thStyle}>Departure</th>
                      <th style={styles.thStyle}>Arrival</th>
                      <th style={styles.thStyle}>Baggage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(notice.flightInfo.outbound || []), ...(notice.flightInfo.inbound || [])].map((flight, index) => {
                      let arrivalDisplay = flight.arrivalTime || '';
                      if (flight.arrivalDate && flight.date && flight.arrivalDate !== flight.date) {
                        arrivalDisplay = `${flight.arrivalTime} (+1 Day)`;
                      }
                      return (
                        <tr key={index}>
                          <td style={styles.tdStyle}>{flight.date}</td>
                          <td style={styles.tdStyle}>{(flight.fromCityEn || flight.fromCity || flight.from)} → {(flight.toCityEn || flight.toCity || flight.to)}</td>
                          <td style={styles.tdStyle}>{flight.flightNo || flight.flight}</td>
                          <td style={styles.tdStyle}>{flight.time}</td>
                          <td style={styles.tdStyle}>{arrivalDisplay}</td>
                          <td style={styles.tdStyle}>{flight.baggage || '2 pcs'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {notice.flightInfo.baggageNote && (
                <p style={{ background: '#fff8e6', padding: isMobile ? '8px' : '10px', border: '1px solid #ffd666', fontSize: isMobile ? '9pt' : '10pt' }}>
                  <strong>Baggage Allowance:</strong> {notice.flightInfo.baggageNote}
                </p>
              )}
              {notice.flightInfo.additionalNote && (
                <p style={{ background: '#fff1f0', padding: isMobile ? '8px' : '10px', border: '1px solid #ffa39e', fontSize: isMobile ? '9pt' : '10pt', marginTop: '10px' }}>
                  <strong>Important Notice:</strong> {notice.flightInfo.additionalNote}
                </p>
              )}
            </div>
          )}

          {/* English - Hotel Information */}
          {(touristHotels?.length > 0 || notice?.hotelInfo?.length > 0 || hasHotelPdf) && (
            <div id="sec-hotel" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>Hotel Information</h2>

              {touristHotels?.length > 0 ? (
                <>
                  <div data-table-wrapper style={styles.tableWrapper}>
                  <table style={{ ...styles.tableStyle, minWidth: isMobile ? '600px' : 'auto' }}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>Check-in</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Nights</th>
                        <th style={styles.thStyle}>Hotel</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Stars</th>
                        <th style={{ ...styles.thStyle, textAlign: 'center' }}>Breakfast</th>
                        <th style={styles.thStyle}>Phone</th>
                        <th style={styles.thStyle}>Booking No.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {touristHotels.map((th, index) => (
                        <tr key={th.id || index}>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.checkInDate}</td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>{calcNights(th.checkInDate, th.checkOutDate)}</td>
                          <td style={styles.tdStyle}>
                            {th.hotel?.nameEn}
                            {(th.hotel?.addressEn || th.hotel?.address) && (
                              <div style={{ fontSize: isMobile ? '7pt' : '8pt', color: '#999', marginTop: '2px' }}>{th.hotel.addressEn || th.hotel.address}</div>
                            )}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center', color: '#faad14' }}>
                            {th.hotel?.starLevel ? '★'.repeat(th.hotel.starLevel) : '-'}
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'center' }}>
                            {th.hotel?.hasBreakfast ? 'Yes' : 'No'}
                          </td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap' }}>{th.hotel?.phone || '-'}</td>
                          <td style={{ ...styles.tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                            {th.bookingNumber || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* Hotel Transport (deduplicate) */}
                  {touristHotels.some(th => th.hotel?.transportationEn || th.hotel?.transportation) && (() => {
                    const groups = {};
                    touristHotels.filter(th => th.hotel?.transportationEn || th.hotel?.transportation).forEach(th => {
                      const info = th.hotel.transportationEn || th.hotel.transportation;
                      const name = th.hotel.nameEn || th.hotel.name;
                      if (!groups[info]) groups[info] = [];
                      groups[info].push(name);
                    });
                    return (
                      <div style={{ marginTop: '15px', padding: isMobile ? '8px' : '10px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '4px' }}>
                        <strong style={{ color: '#1a5490' }}>Hotel Transport (subject to actual conditions):</strong>
                        <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: isMobile ? '9pt' : '10pt' }}>
                          {Object.entries(groups).map(([info, names], index) => (
                            <li key={index} style={{ marginBottom: '5px' }}>
                              <strong>{names.join(', ')}:</strong> {info}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                  {/* Hotel Notes (deduplicate by similarity) */}
                  {touristHotels.some(th => th.hotel?.notesEn || th.hotel?.notes) && (() => {
                    // Group by first 100 chars to merge similar notes
                    const entries = touristHotels.filter(th => th.hotel?.notesEn || th.hotel?.notes).map(th => ({
                      note: th.hotel.notesEn || th.hotel.notes,
                      name: th.hotel.nameEn || th.hotel.name
                    }));
                    const groups = [];
                    entries.forEach(({ note, name }) => {
                      const key = note.substring(0, 100);
                      const existing = groups.find(g => g.key === key);
                      if (existing) {
                        existing.names.push(name);
                      } else {
                        groups.push({ key, note, names: [name] });
                      }
                    });
                    return (
                      <div style={{ marginTop: '15px', padding: isMobile ? '8px' : '10px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '4px' }}>
                        <strong style={{ color: '#d48806' }}>Hotel Notes:</strong>
                        <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', fontSize: isMobile ? '9pt' : '10pt' }}>
                          {groups.map((g, index) => (
                            <li key={index} style={{ marginBottom: '5px' }}>
                              <strong>{g.names.join(', ')}:</strong> {g.note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </>
              ) : notice?.hotelInfo?.length > 0 ? (
                <div data-table-wrapper style={styles.tableWrapper}>
                  <table style={styles.tableStyle}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>City</th>
                        <th style={styles.thStyle}>Hotel Name</th>
                        <th style={styles.thStyle}>Address</th>
                        <th style={styles.thStyle}>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notice.hotelInfo.map((hotel, index) => (
                        <tr key={index}>
                          <td style={styles.tdStyle}>{hotel.city}</td>
                          <td style={styles.tdStyle}>{hotel.name}</td>
                          <td style={{ ...styles.tdStyle, fontSize: isMobile ? '8pt' : '9pt' }}>{hotel.address}</td>
                          <td style={styles.tdStyle}>{hotel.phone || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : hasHotelPdf && (
                <div style={{ padding: isMobile ? '10px' : '12px 15px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px', fontSize: isMobile ? '9pt' : '10pt', color: '#389e0d' }}>
                  Please refer to the PDF attachment for hotel details (see "Your Booking Documents" below).
                </div>
              )}
            </div>
          )}

          {/* English - Optional Add-ons */}
          {touristAddOns?.length > 0 && (
            <div id="sec-addons" style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h2 style={styles.sectionTitle}>Optional Add-ons</h2>
              {touristAddOns.map((ta, index) => (
                <div key={ta.id || index} style={{
                  border: '1px solid #e8e8e8',
                  padding: '12px',
                  marginBottom: '10px',
                  background: '#fafafa'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong style={{ color: '#1a5490' }}>
                      Day {ta.addOnProduct?.dayNumber}: {ta.addOnProduct?.nameEn || ta.addOnProduct?.name}
                    </strong>
                  </p>
                  {ta.addOnProduct?.meetingTime && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>Meeting Time:</strong> {ta.addOnProduct.meetingTime}
                      {ta.addOnProduct?.duration && ` | Duration: ${ta.addOnProduct.duration}`}
                    </p>
                  )}
                  {(ta.addOnProduct?.meetingPlaceEn || ta.addOnProduct?.meetingPlace) && (
                    <p style={{ margin: '5px 0', fontSize: '10pt' }}>
                      <strong>Meeting Place:</strong> {ta.addOnProduct.meetingPlaceEn || ta.addOnProduct.meetingPlace}
                    </p>
                  )}
                  {(ta.addOnProduct?.summaryEn || ta.addOnProduct?.summary) && (
                    <p style={{ margin: '5px 0', fontSize: '10pt', color: '#666' }}>
                      {ta.addOnProduct.summaryEn || ta.addOnProduct.summary}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* English - Add-On Notice Templates */}
          {addOnNoticeTemplates?.length > 0 && addOnNoticeTemplates.map((template, index) => (
            <div key={template.id} id={index === 0 ? 'sec-addon-notice' : undefined} style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>
                Add-on Details: {template.productNameEn || template.productName}
              </h2>

              {/* Meeting Info */}
              {(template.meetingTime || template.meetingPlaceEn || template.meetingPlace) && (
                <div style={{
                  background: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '15px'
                }}>
                  {template.meetingTime && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>Meeting Time:</strong> {template.meetingTime}
                    </p>
                  )}
                  {(template.meetingPlaceEn || template.meetingPlace) && (
                    <p style={{ margin: '5px 0' }}>
                      <strong>Meeting Place:</strong> {template.meetingPlaceEn || template.meetingPlace}
                    </p>
                  )}
                </div>
              )}

              {/* Flight Info */}
              {template.hasFlightInfo && (template.flightDetailsEn || template.flightDetails) && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【Flight Information】
                  </p>
                  <div style={{
                    background: '#fff8e6',
                    border: '1px solid #ffd666',
                    padding: '12px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {template.flightDetailsEn || template.flightDetails}
                  </div>
                </div>
              )}

              {/* Itinerary */}
              {(template.itineraryEn || template.itinerary) && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【Itinerary】
                  </p>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {template.itineraryEn || template.itinerary}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(template.notesEn || template.notes) && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【Important Notes】
                  </p>
                  <div style={{
                    background: '#fff1f0',
                    border: '1px solid #ffa39e',
                    padding: '12px',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {template.notesEn || template.notes}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {template.attachments?.length > 0 && (
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    【Attachments】
                  </p>
                  <ul style={{ paddingLeft: '20px', margin: 0 }}>
                    {template.attachments.map((att) => (
                      <li key={att.id} style={{ marginBottom: '5px' }}>
                        {att.isDownloadable && att.signedUrl ? (
                          <a
                            href={toAbsUrl(att.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {att.nameEn || att.name}
                          </a>
                        ) : (
                          <span>{att.nameEn || att.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}

          {/* English - Destination Information */}
          {destination && (destination.gatheringInfoEn || destination.domesticTransportInfoEn || destination.entryGuideEn || destination.customsInfoEn || destination.vaccineInfoEn || destination.localNoticesEn || destination.insuranceInfoEn || destination.paymentInfoEn || destination.weatherInfoEn) && (
            <>
              {destination.gatheringInfoEn && (
                <div id="sec-gathering" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Gathering & Departure</h2>
                  <MarkdownContent text={destination.gatheringInfoEn} />
                </div>
              )}

              {destination.domesticTransportInfoEn && (
                <div id="sec-domestic-transport" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Domestic Transport</h2>
                  <MarkdownContent text={destination.domesticTransportInfoEn} />
                </div>
              )}

              {destination.entryGuideEn && (
                <div id="sec-entry" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Entry Guide</h2>
                  <MarkdownContent text={destination.entryGuideEn} />
                </div>
              )}

              {destination.customsInfoEn && (
                <div id="sec-customs" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Customs Declaration</h2>
                  <MarkdownContent text={destination.customsInfoEn} />
                </div>
              )}

              {destination.vaccineInfoEn && (
                <div id="sec-vaccine" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Vaccine Requirements</h2>
                  <MarkdownContent text={destination.vaccineInfoEn} />
                </div>
              )}

              {destination.localNoticesEn && (
                <div id="sec-local" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Local Notices</h2>
                  <MarkdownContent text={destination.localNoticesEn} />
                </div>
              )}

              {destination.insuranceInfoEn && (
                <div id="sec-insurance" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Travel Insurance</h2>
                  <MarkdownContent text={destination.insuranceInfoEn} />
                </div>
              )}

              {destination.paymentInfoEn && (
                <div id="sec-payment" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Payment Information</h2>
                  <MarkdownContent text={destination.paymentInfoEn} />
                </div>
              )}

              {destination.weatherInfoEn && (
                <div id="sec-weather" style={{ marginBottom: '25px' }}>
                  <h2 style={styles.sectionTitle}>Weather Information</h2>
                  <MarkdownContent text={destination.weatherInfoEn} />
                </div>
              )}
            </>
          )}

          {/* English - Template Content */}
          {templates?.map((template, index) => {
            return (
              <div key={template.id} id={`sec-tpl-${index}`} style={{ marginBottom: '25px' }}>
                <h2 style={styles.sectionTitle}>
                  {template.titleEn || template.title}
                </h2>
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {template.contentEn || template.content}
                </div>
              </div>
            );
          })}

          {/* English - Other Notes */}
          {(notice?.otherNotesEn || notice?.otherNotes) && (
            <div id="sec-other" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Other Important Notes</h2>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {notice.otherNotesEn || notice.otherNotes}
              </div>
            </div>
          )}

          {/* English - Your Booking Documents */}
          {touristDocuments?.length > 0 && (
            <div id="sec-documents" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Your Booking Documents</h2>
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                padding: '15px',
                marginBottom: '10px'
              }}>
                <p style={{ margin: '0 0 10px 0', color: '#52c41a', fontWeight: 'bold' }}>
                  Please click to download your booking confirmations:
                </p>
                {/* Hotel Confirmations */}
                {touristDocuments.filter(d => d.documentType === 'hotel').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Hotel Confirmations】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'hotel').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Flight Tickets */}
                {touristDocuments.filter(d => d.documentType === 'flight').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Flight Tickets】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'flight').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Add-on Documents */}
                {touristDocuments.filter(d => d.documentType === 'addon').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Add-on Documents】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'addon').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Attachments */}
                {touristDocuments.filter(d => d.documentType === 'attachment').length > 0 && (
                  <div style={{ marginBottom: '10px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>【Attachments】</p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      {touristDocuments.filter(d => d.documentType === 'attachment').map(doc => (
                        <li key={doc.id} style={{ marginBottom: '5px' }}>
                          <a
                            href={toAbsUrl(doc.signedUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1677ff' }}
                          >
                            {doc.documentName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* English - Attachments */}
          {attachments?.length > 0 && (
            <div id="sec-attachments" style={{ marginBottom: '25px' }}>
              <h2 style={styles.sectionTitle}>Attachments</h2>
              {attachments.map((att) => (
                <div key={att.id} style={{ marginBottom: '20px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1a5490' }}>
                    {att.nameEn || att.name}
                    {att.description && <span style={{ fontWeight: 'normal', color: '#666' }}> - {att.description}</span>}
                  </p>
                  {att.fileType === 'image' && (att.signedUrl || att.filePath) && (
                    <img
                      src={toAbsUrl(att.signedUrl || att.filePath)}
                      alt={att.nameEn || att.name}
                      style={{
                        maxWidth: '100%',
                        height: 'auto',
                        border: '1px solid #e8e8e8',
                        borderRadius: '4px'
                      }}
                    />
                  )}
                  {att.fileType === 'pdf' && (att.signedUrl || att.filePath) && (
                    <a
                      href={toAbsUrl(att.signedUrl || att.filePath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1677ff' }}
                    >
                      Click to view PDF file
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}


          {/* English Footer */}
          <div style={{
            marginTop: '40px',
            paddingTop: '20px',
            borderTop: '3px double #1a5490',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a5490', marginBottom: '15px' }}>
              Have a Pleasant Journey!
            </p>
            <div style={{ fontSize: '10pt', color: '#666', lineHeight: 1.6 }}>
              <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#1a5490' }}>
                Worldway Travel
              </p>
              <p style={{ margin: '5px 0' }}>
                Address: 14C/155 Queen Street Auckland Central, Auckland, New Zealand
              </p>
              <p style={{ margin: '5px 0' }}>
                Tel: 0800 116 688 | Email: confirmation@worldway.co.nz
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* 打印样式 */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
};

export default TouristNotice;
